import { useEffect, useMemo, useState } from 'react';
import { ScheduleNowSession } from '../../types/types';
import { getScheduleNow } from '../../hooks/useSchedule';
import { LiveNowState, useLiveNow } from '../../hooks/useLiveNow';
import { MergedLive, mergeLive } from '../../utils/live';
import { LiveState } from './LiveBadge';

/* What's on air now, for the live badges and banners: meta/liveNow (hooks/useLiveNow, re-read
   every `refreshMs` while shown) merged with the meta/scheduleNow rows (utils/live mergeLive).
   Without a fresh feed mergeLive falls back to the time rule (isLiveNow), as before the feed.
   Both reads are optional: a failed read leaves nothing live rather than an error. */

// meta/scheduleNow rows (yesterday to tomorrow): [] until read, and when the read fails
export const useScheduleNowSessions = (): ScheduleNowSession[] => {
  const [sessions, setSessions] = useState<ScheduleNowSession[]>([]);
  useEffect(() => {
    let cancelled = false;
    getScheduleNow()
      .then(now => { if (!cancelled) setSessions(now.sessions); })
      .catch(() => { /* ok: live badges are optional */ });
    return () => { cancelled = true; };
  }, []);
  return sessions;
};

export interface OnAirState {
  merged: MergedLive<ScheduleNowSession>;
  liveNow: LiveNowState;
  // The feed's first read is done (or failed): until then show nothing live, so a badge
  // doesn't flip from the time rule to the feed
  ready: boolean;
}

export const useOnAir = (options: { refreshMs?: number } = {}): OnAirState => {
  const sessions = useScheduleNowSessions();
  const liveNow = useLiveNow(options);
  const { live, clock, fresh } = liveNow;
  const merged = useMemo(() => mergeLive(sessions, live, clock, { fresh }), [sessions, live, clock, fresh]);
  return { merged, liveNow, ready: live !== undefined || liveNow.error };
};

// A committee's badge: on air, on a break (only when none of its sessions is on air), or neither
export const committeeLiveState = ({ merged, ready }: OnAirState, committeeDocId: string): LiveState => {
  if (!ready || !merged.committeeDocIds.has(committeeDocId)) return null;
  return merged.pausedCommitteeDocIds.has(committeeDocId) ? 'paused' : 'live';
};
